import date from "date-and-time";

const DISPLAY_DATE_FORMAT_SHORT = "DD MMM YYYY";
const DISPLAY_DATE_FORMAT_LONG = "DD MMMM YYYY";

export const getSelectedLanguage = (fallbackLocale = navigator.language): string => {
  const languageFromStorage = typeof window !== "undefined" ? localStorage.getItem("language") : null;

  if (!languageFromStorage) {
    return fallbackLocale;
  }

  const getLocaleCandidates = (value: string): string[] => {
    const normalizedValue = value
      .trim()
      .replace(/^['"]|['"]$/g, "")
      .replace(/_/g, "-");

    if (!normalizedValue) {
      return [];
    }

    const lowerCaseValue = normalizedValue.toLowerCase();
    if (lowerCaseValue === "ka") {
      return ["ka-GE", normalizedValue];
    }

    return [normalizedValue];
  };

  const normalizeLocale = (value: string): string | null => {
    const candidates = getLocaleCandidates(value);
    for (const candidate of candidates) {
      if (Intl.DateTimeFormat.supportedLocalesOf([candidate]).length) {
        return candidate;
      }
    }
    return null;
  };

  const normalizedLocale = normalizeLocale(languageFromStorage);
  if (normalizedLocale) {
    return normalizedLocale;
  }

  try {
    const parsedValue = JSON.parse(languageFromStorage);
    if (typeof parsedValue === "string") {
      return normalizeLocale(parsedValue) || fallbackLocale;
    }

    if (parsedValue && typeof parsedValue === "object") {
      const candidate = parsedValue.language || parsedValue.lang || parsedValue.locale || parsedValue.code;
      if (typeof candidate === "string") {
        return normalizeLocale(candidate) || fallbackLocale;
      }
    }
  } catch {
    // no-op: keep fallback locale
  }

  return fallbackLocale;
};

const isEnglishLocale = (locale: string): boolean => {
  return locale.toLowerCase().startsWith("en");
};

const formatLocalizedDisplayDate = (dateValue: Date, locale = getSelectedLanguage()): string => {
  const day = String(dateValue.getUTCDate()).padStart(2, "0");
  const year = String(dateValue.getUTCFullYear());
  const monthLength: "short" | "long" = isEnglishLocale(locale) ? "short" : "long";
  const month = new Intl.DateTimeFormat(locale, { month: monthLength, timeZone: "UTC" }).format(dateValue);
  return `${day} ${month} ${year}`;
};

const formatDate = (dateValue: Date, format?: string, useUtc = false): string => {
  const resolvedFormat = format ?? getLocaleDateFormat();

  if (resolvedFormat === DISPLAY_DATE_FORMAT_SHORT || resolvedFormat === DISPLAY_DATE_FORMAT_LONG) {
    return formatLocalizedDisplayDate(dateValue);
  }

  return date.format(dateValue, resolvedFormat, useUtc);
};

export const removeOffsetFromDateString = (dateStr: string): string => {
  const format = dateStr.substring(0, 16);
  return format;
};

/**
 * Parses a date string (with or without timezone offset) into a Date whose
 * UTC components exactly match the source-timezone date/time components.
 * This means every caller can read getUTCDate/getUTCMonth/getUTCFullYear and
 * get the same digits in every timezone — display is location-independent.
 */
const parseLocalDateTime = (dateStr: string): Date => {
  const stripped = removeOffsetFromDateString(dateStr);
  const [datePart, timePart = "00:00"] = stripped.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hours, minutes] = timePart.split(":").map(Number);
  return new Date(Date.UTC(year, month - 1, day, hours, minutes));
};

export const isValidString = (value: any): boolean => {
  return value !== null && value !== undefined && value.trim() !== "";
};

export const dateFormatWithoutOffset = (now: string, format?: string): string => {
  if (isValidString(now)) {
    const dateTime = parseLocalDateTime(now);
    const formattedDate = formatDate(dateTime, format, true);
    return formattedDate;
  }
  return "";
};

export const dateAndTimeWithoutOffset = (dateString: string, format?: string): string => {
  const dateTime = parseLocalDateTime(dateString);
  const formattedDate = formatDate(dateTime, format, true);
  const formattedTime = date.format(dateTime, format ?? vesselTime, true); // true = UTC mode
  return `${formattedDate} - ${formattedTime}`;
};

// Create a function to format the date and time expecting Date object
export const dateFormatter = (dateValue: string, format?: string): string => {
  // Format the date using the provided format of type Date
  const formattedDate = formatDate(new Date(dateValue), format);
  return formattedDate;
};

export const vesselActualDateFormatter = (actual: string, estimated: string): string => {
  if (isValidString(actual)) {
    return formatter(actual, vesselDate);
  } else if (isValidString(estimated)) {
    return formatter(estimated, vesselDate);
  }
  return "";
};

export const vesselActualTimeFormatter = (actual: string, estimated: string): string => {
  if (isValidString(actual)) {
    const date = formatter(actual, vesselTime);
    return date;
  } else if (isValidString(estimated)) {
    const date = formatter(estimated, vesselTime);
    return date;
  }
  return "";
};

export const vesselTimeDifferentiate = (actual: string, estimated: string, sendDeparture = false): string => {
  if (isValidString(actual)) {
    return sendDeparture ? "ATD" : "ATA";
  } else if (isValidString(estimated)) {
    return sendDeparture ? "ETD" : "ETA";
  }
  return "";
};

export const getDateBgColor = (actual: string, estimated: string): string => {
  if (isValidString(actual)) {
    return "actual";
  } else if (isValidString(estimated)) {
    return "estimated";
  }
  return "";
};

export const getDateIcon = (actual: string, estimated: string): string => {
  if (isValidString(actual)) {
    return "check";
  } else if (isValidString(estimated)) {
    return "clock";
  }
  return "";
};

export const getDateIconColor = (actual: string, estimated: string): string => {
  if (isValidString(actual)) {
    return "#ff6441";
  } else if (isValidString(estimated)) {
    return "#42b0d5";
  }
  return "";
};

export const vesselScheduleDateFormatter = (schedule: string): string => {
  if (isValidString(schedule)) {
    return formatter(schedule, vesselDate);
  }
  return "";
};

export const vesselScheduleTimeFormatter = (schedule: string): string => {
  if (isValidString(schedule)) {
    return formatter(schedule, vesselTime);
  }
  return "";
};

export const formatter = (dateString: string, format?: string): string => {
  const dateTime = parseLocalDateTime(dateString);
  const formattedDate = formatDate(dateTime, format, true);
  return formattedDate;
};

export const getLocaleDateFormat = (locale = navigator.language) => {
  const resolvedLocale = getSelectedLanguage(locale);
  return isEnglishLocale(resolvedLocale) ? DISPLAY_DATE_FORMAT_SHORT : DISPLAY_DATE_FORMAT_LONG;
};

export const getLocaleTimeFormat = (locale = navigator.language) => {
  const resolvedLocale = getSelectedLanguage(locale);
  const options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: undefined // let locale decide (true in en-US, false in en-GB, de-DE, etc.)
  };

  const parts = new Intl.DateTimeFormat(resolvedLocale, options).formatToParts(
    new Date(2025, 1, 24, 18, 0) // 6:00 PM
  );

  let format = "";
  for (const part of parts) {
    switch (part.type) {
      case "hour":
        // if locale uses 12h clock, use "hh", else "HH"
        format += parts.some((p) => p.type === "dayPeriod") ? "hh" : "HH";
        break;
      case "minute":
        format += "mm";
        break;
      case "dayPeriod":
        format += " A"; // AM/PM if present
        break;
      case "literal":
        format += part.value;
        break;
    }
  }
  return format.trim();
};

const vesselDate = getLocaleDateFormat();
const vesselTime = getLocaleTimeFormat();
